// src/lib/env.ts
// Centralized environment variable management with runtime validation

export const env = {
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL as string,
  // Accept either ANON or PUBLISHABLE for backward compatibility
  SUPABASE_ANON_KEY:
    (import.meta.env.VITE_SUPABASE_ANON_KEY as string) ??
    (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string),
  GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || '',
  APP_VERSION: import.meta.env.VITE_APP_VERSION || 'development',
};

// Runtime validation for required variables
const missingSupabaseVars = !env.SUPABASE_URL || !env.SUPABASE_ANON_KEY;
const shouldEnforceEnv = import.meta.env.PROD || import.meta.env.MODE === 'production';
const envMissingMessage =
  'Missing required environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY';

const looksLikeSupabasePublicKey = (value: string) => {
  // Support both key formats:
  // 1) Legacy anon key: JWT-shaped (3 dot-separated parts), usually starts with "eyJ"
  // 2) New public keys: "sb_publishable_*"
  const trimmed = value?.trim?.() ?? '';
  if (trimmed.startsWith('sb_publishable_')) return true;
  return trimmed.split('.').length === 3 && trimmed.startsWith('eyJ');
};

const invalidSupabaseKey =
  !!env.SUPABASE_ANON_KEY && !looksLikeSupabasePublicKey(env.SUPABASE_ANON_KEY);
const invalidSupabaseKeyMessage =
  'Invalid VITE_SUPABASE_ANON_KEY: expected a Supabase public key (either "sb_publishable_*" or a JWT-shaped legacy anon key starting with "eyJ").';

if (missingSupabaseVars) {
  if (shouldEnforceEnv) {
    // Fail fast in production to prevent runtime errors
    console.warn(`[env] ${envMissingMessage}`);
    throw new Error(envMissingMessage);
  }

  // In development, test, and tooling contexts we only warn to keep workflows unblocked
  const modeLabel = import.meta.env.MODE ?? 'unknown';
  console.warn(`[env] ${envMissingMessage}. Running in ${modeLabel} mode; continuing without throwing.`);
}

if (invalidSupabaseKey) {
  if (shouldEnforceEnv) {
    console.warn(`[env] ${invalidSupabaseKeyMessage}`);
    throw new Error(invalidSupabaseKeyMessage);
  }

  const modeLabel = import.meta.env.MODE ?? 'unknown';
  console.warn(
    `[env] ${invalidSupabaseKeyMessage} Running in ${modeLabel} mode; requests will likely fail.`
  );
}
